import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PillChip } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { ArrowLeft, Package, CheckCircle, Clock, QrCode, Printer, ExternalLink, AlertCircle, AlertTriangle } from "lucide-react";

const WEAR_DURATIONS = [1, 2, 3, 5, 7, 10, 14];

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SellerRescuePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const CATEGORIES = [
    { value: "SOCKS", label: t("rescue.cat_socks") },
    { value: "SLIP", label: t("rescue.cat_slip") },
  ];
  const [slots, setSlots] = useState(null);
  const [orders, setOrders] = useState([]);
  const [bags, setBags] = useState([]);
  const [damagedCases, setDamagedCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [activateFor, setActivateFor] = useState(null); // bag_id
  const [category, setCategory] = useState("SOCKS");
  const [wear, setWear] = useState(3);

  const load = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true); setError(""); setErrorCode("");
    try {
      const [s, o, b, d] = await Promise.all([
        api.get("/rescue/slots/summary"),
        api.get("/rescue/kit/orders"),
        api.get("/rescue/bags"),
        api.get("/rescue/damaged/cases?status=AWAITING_SELLER_DECISION").catch(() => ({ data: { cases: [] } })),
      ]);
      setSlots(s.data);
      setOrders(o.data.orders || []);
      setBags(b.data.bags || []);
      setDamagedCases(d.data.cases || []);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      setError(code || "LOAD_FAILED");
    }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    load();
  }, [user, navigate, load]);

  // Auto-clear stale SLOT_LIMIT_REACHED banner when quota has room again
  useEffect(() => {
    if (slots && typeof slots.remaining === "number" && slots.remaining > 0 && errorCode === "SLOT_LIMIT_REACHED") {
      setError(""); setErrorCode("");
    }
  }, [slots, errorCode]);

  const buyKit = async (payment_method) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/rescue/kit/order", { payment_method });
      alert(t("rescue.purchased_ok") + "\n\n" + (data.payment_hint || ""));
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail?.code || "ORDER_FAILED");
    } finally { setBusy(false); }
  };

  const decideDamagedCase = async (case_id, decision) => {
    if (!window.confirm(decision === "RETURN" ? t("rescue.confirm_return") : t("rescue.confirm_sell_anyway"))) return;
    setBusy(true); setError("");
    try {
      await api.post(`/rescue/damaged/cases/${case_id}/decide`, { decision });
      await load();
    } catch (e) { setError(e?.response?.data?.detail?.code || "DECIDE_FAILED"); }
    finally { setBusy(false); }
  };

  const activateBag = async (bag_id) => {
    setBusy(true); setError("");
    try {
      await api.post(`/rescue/bags/${bag_id}/activate`, {
        category, wear_duration_days: wear,
      });
      setActivateFor(null);
      await load();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      setErrorCode(code || "ACTIVATE_FAILED");
      setError(code === "SLOT_LIMIT_REACHED" ? t("rescue.slot_limit_reached") : (code || "ACTIVATE_FAILED"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="seller-rescue-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="rescue-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A] flex-1">{t("rescue.title_seller")}</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>
      ) : error === "STX_SELLER_ONLY" ? (
        <div data-testid="rescue-stx-only" className="mx-4 my-6 p-4 border border-amber-300 bg-amber-50 rounded">
          <p className="text-sm font-semibold text-amber-900">{t("rescue.stx_only_title")}</p>
          <p className="text-xs text-amber-800 mt-1">{t("rescue.stx_only_body")}</p>
          <button
            data-testid="rescue-stx-goto-caps"
            onClick={() => navigate("/profile/access")}
            className="mt-3 px-3 py-1.5 text-xs font-medium bg-[#0A0A0A] text-white rounded">
            {t("rescue.stx_only_cta")}
          </button>
        </div>
      ) : (
        <>
          {/* Slot widget */}
          <div data-testid="rescue-slot-widget" className="mx-4 my-3 p-4 border border-[#E5E5E5] rounded bg-[#FAFAFA]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-[#666] uppercase tracking-wider">{t("rescue.monthly_limit")}</p>
              <span className="text-[10px] text-[#999]">{slots?.month || ""}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-[#0A0A0A]">{slots?.remaining ?? "—"}</span>
              <span className="text-sm text-[#666]">{t("rescue.of_available", { limit: slots?.limit ?? 3 })}</span>
            </div>
            <div className="mt-2 h-1.5 bg-[#E5E5E5] rounded overflow-hidden">
              <div className="h-full bg-[#0A0A0A]" style={{width: `${slots ? (slots.used/slots.limit)*100 : 0}%`}} />
            </div>
          </div>

          {/* Buy Kit */}
          <div className="mx-4 mb-4 p-4 border border-[#0A0A0A] rounded">
            <p className="text-sm font-bold text-[#0A0A0A]">{t("rescue.buy_kit_title")}</p>
            <p className="text-xs text-[#666] mt-1">{t("rescue.buy_kit_desc")}</p>
            <div className="flex gap-2 mt-3">
              <button
                data-testid="rescue-buy-paypal"
                onClick={() => buyKit("PAYPAL")}
                disabled={busy}
                className="flex-1 px-3 py-2 text-xs font-medium bg-[#0A0A0A] text-white rounded disabled:opacity-50">
                {t("rescue.pay_paypal")}
              </button>
              <button
                data-testid="rescue-buy-vorkasse"
                onClick={() => buyKit("VORKASSE")}
                disabled={busy}
                className="flex-1 px-3 py-2 text-xs font-medium border border-[#0A0A0A] text-[#0A0A0A] rounded disabled:opacity-50">
                {t("rescue.pay_prepayment")}
              </button>
            </div>
          </div>

          {error && (
            <div data-testid="rescue-error" className="mx-4 mb-3 p-2 bg-red-50 text-red-600 text-xs flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Damaged cases requiring decision (F-2) */}
          {damagedCases.length > 0 && (
            <div data-testid="rescue-damaged-block" className="mx-4 mb-3">
              <p className="text-[10px] text-red-700 uppercase tracking-wider font-semibold mb-1 flex items-center gap-1">
                <AlertTriangle size={12} /> {t("rescue.decision_required", { count: damagedCases.length })}
              </p>
              <div className="divide-y divide-[#F0F0F0] border border-red-300 rounded bg-red-50">
                {damagedCases.map(c => (
                  <div key={c.damaged_case_id} data-testid={`rescue-damaged-${c.damaged_case_id}`} className="p-3">
                    <p className="text-xs font-medium text-[#0A0A0A]">{t("rescue.damaged_bag", { id: c.bag_id.slice(0,8).toUpperCase() })}</p>
                    <p className="text-[10px] text-[#666] mt-0.5">{t("rescue.damaged_reason", { reason: c.reason || "—" })}</p>
                    <p className="text-[10px] text-[#999] mt-0.5">{t("rescue.damaged_deadline", { date: c.decision_deadline_at?.slice(0,10), fee: c.return_fee_eur?.toFixed(2) })}</p>
                    {c.photo_urls?.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {c.photo_urls.slice(0,3).map((u, i) => (
                          <img key={i} src={u} alt="" className="w-12 h-12 object-cover border border-[#E5E5E5]" />
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 mt-2">
                      <button
                        data-testid={`rescue-damaged-sell-${c.damaged_case_id}`}
                        onClick={() => decideDamagedCase(c.damaged_case_id, "SELL_ANYWAY")}
                        disabled={busy}
                        className="flex-1 px-2 py-1 text-[10px] font-medium border border-[#0A0A0A] text-[#0A0A0A] rounded">
                        {t("rescue.sell_anyway")}
                      </button>
                      <button
                        data-testid={`rescue-damaged-return-${c.damaged_case_id}`}
                        onClick={() => decideDamagedCase(c.damaged_case_id, "RETURN")}
                        disabled={busy}
                        className="flex-1 px-2 py-1 text-[10px] font-medium bg-red-600 text-white rounded">
                        {t("rescue.return_bag", { fee: c.return_fee_eur?.toFixed(2) })}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending orders (info-only for seller — confirmation happens on admin side) */}
          {orders.filter(o => o.status === "PENDING_PAYMENT").length > 0 && (
            <div className="mx-4 mb-3">
              <p className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-1">{t("rescue.payment_pending")}</p>
              <div className="divide-y divide-[#F0F0F0] border border-[#E5E5E5] rounded">
                {orders.filter(o => o.status === "PENDING_PAYMENT").map(o => (
                  <div key={o.kit_order_id} className="p-3 flex items-start gap-2">
                    <Clock size={14} className="text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#0A0A0A]">RESCUE-{o.kit_order_id.slice(0,8).toUpperCase()}</p>
                      <p className="text-[10px] text-[#666]">{o.total_amount?.toFixed(2)} € · {o.payment_method}</p>
                      <p className="text-[10px] text-[#999] mt-1">{t("rescue.payment_pending_hint")}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bag list */}
          <div className="mx-4 mb-6">
            <p className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-1">{t("rescue.my_bags_seller", { count: bags.length })}</p>
            {bags.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-[#E5E5E5] rounded">
                <Package size={28} className="mx-auto text-[#CCC] mb-2" />
                <p className="text-xs text-[#999]">{t("rescue.no_bags")}</p>
              </div>
            ) : (
              <div className="divide-y divide-[#F0F0F0] border border-[#E5E5E5] rounded">
                {bags.map(bag => {
                  const isIssued = bag.status === "ISSUED";
                  const isActive = bag.status === "ACTIVATED";
                  const isActivating = activateFor === bag.bag_id;
                  return (
                    <div key={bag.bag_id} data-testid={`rescue-bag-${bag.bag_id}`} className="p-3">
                      <div className="flex items-center gap-2">
                        {isActive ? <CheckCircle size={16} className="text-green-600" /> : <QrCode size={16} className="text-[#666]" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-[#0A0A0A]">{t("rescue.bag_id", { id: bag.bag_id.slice(0,8).toUpperCase() })}</p>
                          <p className="text-[10px] text-[#666]">
                            {isActive ? `${bag.activation?.category} · ${bag.activation?.wear_duration_days}d` : t("rescue.not_activated")}
                          </p>
                        </div>
                        {isIssued && (
                          <button
                            data-testid={`rescue-activate-open-${bag.bag_id}`}
                            onClick={() => setActivateFor(isActivating ? null : bag.bag_id)}
                            className="px-2 py-1 text-[10px] font-medium border border-[#0A0A0A] text-[#0A0A0A] rounded">
                            {t("rescue.activate_open")}
                          </button>
                        )}
                        {isActive && (
                          <>
                            <a
                              data-testid={`rescue-label-pdf-${bag.bag_id}`}
                              href={`${REACT_APP_BACKEND_URL}/api/rescue/bags/${bag.bag_id}/label.pdf`}
                              target="_blank" rel="noreferrer"
                              className="p-1 text-[#0A0A0A]" aria-label="PDF Label">
                              <Printer size={14} />
                            </a>
                            <a
                              data-testid={`rescue-label-png-${bag.bag_id}`}
                              href={`${REACT_APP_BACKEND_URL}/api/rescue/bags/${bag.bag_id}/label.png`}
                              target="_blank" rel="noreferrer"
                              className="p-1 text-[#666]" aria-label="PNG Label">
                              <ExternalLink size={14} />
                            </a>
                          </>
                        )}
                      </div>
                      {isActivating && (
                        <div data-testid={`rescue-activate-form-${bag.bag_id}`} className="mt-3 p-3 bg-[#FAFAFA] rounded space-y-2">
                          <div>
                            <p className="text-[10px] text-[#999] uppercase mb-1">{t("rescue.category_label")}</p>
                            <div className="flex gap-1.5">
                              {CATEGORIES.map(c => (
                                <PillChip
                                  key={c.value}
                                  testId={`rescue-cat-${c.value}`}
                                  active={category === c.value}
                                  onClick={() => setCategory(c.value)}
                                  variant="neutral"
                                  size="sm"
                                >
                                  {c.label}
                                </PillChip>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#999] uppercase mb-1">{t("rescue.wear_days_label")}</p>
                            <div className="flex gap-1 flex-wrap">
                              {WEAR_DURATIONS.map(d => (
                                <PillChip
                                  key={d}
                                  testId={`rescue-wear-${d}`}
                                  active={wear === d}
                                  onClick={() => setWear(d)}
                                  variant="neutral"
                                  size="sm"
                                >
                                  {d}
                                </PillChip>
                              ))}
                            </div>
                          </div>
                          <button
                            data-testid={`rescue-activate-confirm-${bag.bag_id}`}
                            onClick={() => activateBag(bag.bag_id)}
                            disabled={busy}
                            className="w-full mt-2 px-3 py-2 text-xs font-medium bg-[#0A0A0A] text-white rounded disabled:opacity-50">
                            {t("rescue.activate_confirm")}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
